import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import useStore from "../hooks/use-store";
import ClothingHeader from "./ClothingHeader";
import xIcon from "../assets/images/x.webp";

// ─── Types ────────────────────────────────────────────────────────────────────

interface Props {
  className?: string;
}

type Status =
  | { type: "idle" }
  | { type: "loading" }
  | { type: "success"; message: string }
  | { type: "error"; message: string };

// ─── Reusable FormField ───────────────────────────────────────────────────────

interface FormFieldProps {
  label: string;
  type?: string;
  placeholder?: string;
  value: string | number;
  onChange: (value: string) => void;
  disabled?: boolean;
}

const FormField = ({
  label,
  type = "text",
  placeholder,
  value,
  onChange,
  disabled,
}: FormFieldProps) => (
  <div className="flex flex-col gap-1.5">
    <label className="text-white/50 text-xs uppercase tracking-wider">
      {label}
    </label>
    <input
      type={type}
      placeholder={placeholder}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      className="w-full bg-black/30 rounded-md p-3 placeholder:text-white/60 text-white outline-none border-none disabled:opacity-40"
    />
  </div>
);

// ─── Status Banner ────────────────────────────────────────────────────────────

const StatusBanner = ({ status }: { status: Status }) => {
  if (status.type === "idle" || status.type === "loading") return null;

  const isSuccess = status.type === "success";

  return (
    <div
      className={`mb-4 px-4 py-2.5 rounded-md text-sm font-medium transition-all ${
        isSuccess
          ? "bg-green-500/15 border border-green-500/40 text-green-400"
          : "bg-red-500/15 border border-red-500/40 text-red-400"
      }`}
    >
      {status.message}
    </div>
  );
};

// ─── Constants ────────────────────────────────────────────────────────────────

const CLOSE_DELAY_MS = 1500;

// ─── Component ────────────────────────────────────────────────────────────────

const EditProductModal = ({ className = "" }: Props) => {
  const {
    modalData,
    setModalData,
    updateProduct: updateProductInStore,
  } = useStore();

  const [title, setTitle] = useState(modalData?.product.title ?? "");
  const [price, setPrice] = useState<string | number>(
    modalData?.product.price ?? "",
  );
  const [stock, setStock] = useState<string | number>(
    modalData?.product.stock ?? "",
  );
  const [status, setStatus] = useState<Status>({ type: "idle" });

  useEffect(() => {
    if (!modalData?.product) return;
    const { title, price, stock } = modalData.product;
    setTitle(title);
    setPrice(price);
    setStock(stock);
  }, [modalData?.product]);

  const handleClose = () => {
    setModalData(null);
    setStatus({ type: "idle" });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus({ type: "loading" });

    try {
      updateProductInStore(modalData!.product.id, {
        title,
        price: +price,
        stock: +stock,
      });

      setStatus({ type: "success", message: "Product updated successfully!" });
      setTimeout(handleClose, CLOSE_DELAY_MS);
    } catch {
      setStatus({
        type: "error",
        message: "Failed to update product. Please try again.",
      });
    }
  };

  const isBusy = status.type === "loading" || status.type === "success";

  return (
    <AnimatePresence mode="wait">
      {modalData && (
        <>
          {/* Backdrop */}
          <motion.div
            key="backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25, ease: "easeOut" }}
            className="fixed inset-0 z-[90] bg-black/60 backdrop-blur-sm"
            onClick={handleClose}
          />

          {/* Modal */}
          <motion.div
            key="modal"
            initial={{
              opacity: 0,

              filter: "blur(10px)",
            }}
            animate={{
              opacity: 1,

              filter: "blur(0px)",
            }}
            exit={{
              opacity: 0,

              filter: "blur(6px)",
            }}
            transition={{
              duration: 0.15,
              ease: [0.22, 1, 0.36, 1],
            }}
            className={`z-[100] fixed top-[50%] left-[50%] translate-x-[-50%] translate-y-[-50%] min-w-[580px] rounded-md min-h-[330px] p-8 bg-gradient-to-r from-[#283145] to-[#101420]/90 ${className}`}
          >
            {/* Header */}
            <div className="rounded-md border border-white/15 py-3 px-5 flex items-center justify-between mb-6">
              <ClothingHeader
                titleWords={[
                  { text: "Edit" },
                  { text: modalData.product.title, isHighlighted: true },
                ]}
                description=""
              />
              <button
                title="Close"
                onClick={handleClose}
                disabled={isBusy}
                className="border-[#FF4141] border-[1px] rounded-md bg-gradient-to-b from-[#FF4141]/20 via-[#FF4141]/30 to-[#FF4141]/35 p-3 transition hover:opacity-70 active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <img alt="X" src={xIcon} className="w-[14px] h-[14px]" />
              </button>
            </div>

            <StatusBanner status={status} />

            <form onSubmit={handleSubmit} className="flex flex-col gap-4">
              <FormField
                label="Title"
                placeholder="Type title"
                value={title}
                onChange={setTitle}
                disabled={isBusy}
              />
              <FormField
                label="$ Price"
                type="number"
                placeholder="Type price"
                value={price}
                onChange={setPrice}
                disabled={isBusy}
              />
              <FormField
                label="Stock"
                type="number"
                placeholder="Type stock"
                value={stock}
                onChange={setStock}
                disabled={isBusy}
              />

              <button
                type="submit"
                disabled={isBusy}
                className="
                  relative overflow-hidden w-full mt-1 rounded-md p-2
                  text-white text-center text-md 
                  border border-[#56F5FF]/50
                  bg-gradient-to-r from-[#224c56]/20 via-[#224c56]/80 to-[#224c56]/20
                  shadow-[0_0_0px_rgba(86,245,255,0)]
                  hover:shadow-[0_0_18px_rgba(86,245,255,0.35)]
                  hover:border-[#56F5FF]/80
                  transition-all duration-300
                  active:scale-[0.92]
                  disabled:opacity-50 disabled:cursor-not-allowed disabled:active:scale-100
                  group
                "
              >
                <span className="pointer-events-none absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-[#56F5FF]/15 to-transparent group-hover:translate-x-full transition-transform duration-700 ease-in-out" />
                {status.type === "loading" ? "Saving..." : "Save Changes"}
              </button>
            </form>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default EditProductModal;
